import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchOrganisationContent, getMemberNetworks, getOrganisation } from "./organisationsAPI";
import StorageService from "../../util/storage";
import { catchAllErrors } from "../../hoc/catchAllErrors";

export const getOrganisationId = async () => {
    let id = await StorageService.getItem("organisationId");

    if (!id) {
        const org = await catchAllErrors(getOrganisation)();
        id = org.attributes.orgId;
    }
    return id;
};

export const getAllAllowedOrganisationIds = async () => {
    const org = await catchAllErrors(getOrganisation)();
    const ids = org.attributes.allowOtherTenants.data.map((tenant) => tenant.attributes.orgId);

    ids.push(org.attributes.orgId);
    return ids;
}

export const getOrganisationDetails = async () => {
    const org = await catchAllErrors(getOrganisation)();
    return { id: org?.id, ...org?.attributes };
};

export const fetchOrganisationContentAsync = createAsyncThunk("organisations/fetchOrganisationContent", async () => {
    let id = await catchAllErrors(getOrganisationId)();
    const response = await catchAllErrors(fetchOrganisationContent)(id);
    return response;
});

export const fetchMemberNetworksAsync = createAsyncThunk("organisations/fetchMemberNetworks", async () => {
    let id = await catchAllErrors(getOrganisationId)();
    const response = await catchAllErrors(getMemberNetworks)(id);
    return response;
});

export const fetchOrganisationItemAsync = createAsyncThunk("organisations/fetchOrganisationItem", async () => {
    const org = await catchAllErrors(getOrganisation)();
    return org;
});

export const organisationSlice = createSlice({
    name: "organisation",
    initialState: {
        organisation: null,
        meta: {},
        status: "idle",
        error: null,
        content: null,
    },
    reducers: {
        setOrganisation: (state, action) => {
            state.organisation = action.payload;
        },
        setContent: (state, action) => {
            state.content = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrganisationContentAsync.pending, (state) => {
                state.content = null;
                state.status = "pending";
            })
            .addCase(fetchOrganisationContentAsync.fulfilled, (state, action) => {
                state.content = action.payload;
                state.status = "fulfilled";
            })
            .addCase(fetchOrganisationContentAsync.rejected, (state, action) => {
                state.content = null;
                state.status = "rejected";
                state.error = action.error.message;
            })
            .addCase(fetchOrganisationItemAsync.pending, (state) => {
                state.organisation = null;
            })
            .addCase(fetchOrganisationItemAsync.fulfilled, (state, action) => {
                state.organisation = action.payload;
            })
            .addCase(fetchOrganisationItemAsync.rejected, (state, action) => {
                state.organisation = null;
            })
            .addCase(fetchMemberNetworksAsync.pending, (state) => {
                state.meta = {};
            })
            .addCase(fetchMemberNetworksAsync.fulfilled, (state, action) => {
                state.meta = action.payload;
            })
            .addCase(fetchMemberNetworksAsync.rejected, (state, action) => {
                state.meta = {};
            });
    },
});

export const { setOrganisations, setOrganisation } = organisationSlice.actions;

export const selectOrganisation = (state) => state.organisation.organisation;
export const selectContent = (state) => state.organisation.content;

export default organisationSlice.reducer;
